import { Box, Card, Flex, Text } from '@mantine/core';
import { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend, TooltipItem } from 'chart.js';
import theme from 'theme';
import H2SemiBold from 'theme/components/Typography/H2SemiBold';
import { xScaleConfig, yGridConfig } from 'pages/tuya-device/components/chartHelpers';
import useLang from 'lang';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

interface CostDifferenceChartCardProps {
  /** Spot cost minus fixed cost per interval (€), null for missing intervals */
  differenceData: (number | null)[];
  /** Labels for valid (non-null consumption) intervals */
  validIntervalLabels: (string | undefined)[];
  /** Total spot cost for the day */
  totalCost: number;
  /** Total fixed tariff cost for the day */
  totalFixedTariffCost: number;
  /** The date string from the API response */
  date: string;
}

/**
 * Displays a bar chart of the per-interval cost difference (Spot − Fix).
 * Green bars = spot cheaper, red bars = spot more expensive.
 *
 * @prop {(number | null)[]} differenceData - Per-interval difference in €.
 * @prop {(string | undefined)[]} validIntervalLabels - X-axis labels.
 * @prop {number} totalCost - Total spot cost.
 * @prop {number} totalFixedTariffCost - Total fixed tariff cost.
 * @prop {string} date - ISO date string for the chart title.
 */
export default function CostDifferenceChartCard({
  differenceData,
  validIntervalLabels,
  totalCost,
  totalFixedTariffCost,
  date,
}: CostDifferenceChartCardProps) {
  const { _t } = useLang();
  const chartData = useMemo(
    () => ({
      labels: validIntervalLabels,
      datasets: [
        {
          label: _t('Difference (€)'),
          data: differenceData.filter((d) => d !== null),
          backgroundColor: differenceData.map((d) => (d === null ? '#9CA3AF' : d < 0 ? '#10B981' : '#EF4444')),
          hoverBackgroundColor: differenceData.map((d) => (d === null ? '#6B7280' : d < 0 ? '#059669' : '#DC2626')),
          borderRadius: 5,
          borderSkipped: false as const,
          barPercentage: 0.9,
        },
      ],
    }),
    [_t, validIntervalLabels, differenceData]
  );

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: { ...xScaleConfig },
        y: {
          ticks: {
            color: theme.colors?.gray?.[7],
            callback: (v: number | string) => `${Number(v).toFixed(6)} €`,
          },
          grid: yGridConfig,
          beginAtZero: true,
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: true,
          callbacks: {
            label: (ctx: TooltipItem<'bar'>) => {
              const v = ctx.parsed.y;
              return `${v < 0 ? _t('Spot cheaper') : _t('Spot more expensive')}: ${Math.abs(v).toFixed(6)} €`;
            },
          },
        },
      },
    }),
    [_t]
  );

  const totalDiff = totalCost - totalFixedTariffCost;
  const isSpotCheaper = totalCost < totalFixedTariffCost;
  const pct = totalFixedTariffCost > 0 ? (Math.abs(totalDiff) / totalFixedTariffCost) * 100 : 0;

  return (
    <Card p={20} radius={10} shadow="sm" mt={20}>
      <H2SemiBold mb={20}>
        {_t('Comparison: Spot vs Fix')} - {new Date(date).toLocaleDateString('sk-SK')}
      </H2SemiBold>
      <Box style={{ height: 400 }}>
        <Bar data={chartData} options={chartOptions} />
      </Box>

      <Box mt={20} p={15} style={{ backgroundColor: '#F9FAFB', borderRadius: 8, border: '1px solid #E5E7EB' }}>
        <Flex justify="space-between" align="center">
          <Text size="lg" fw={600} c="gray.7">
            {_t('Total difference (spot - fix)')}:
          </Text>
          <Text size="xl" fw={700} c={isSpotCheaper ? '#10B981' : '#EF4444'}>
            {totalDiff.toFixed(4)} €
          </Text>
        </Flex>
        <Text size="sm" c="gray.6" mt={5}>
          {isSpotCheaper
            ? `${_t('Spot is cheaper by')} ${pct.toFixed(2)}%`
            : `${_t('Spot is more expensive by')} ${pct.toFixed(2)}%`}
        </Text>
      </Box>
    </Card>
  );
}
